/**
 * Making sense of whatever the calendar generator returned for a slot.
 *
 * The happy shape is `{ text, title, hashtags }`. In practice the model's whole
 * JSON envelope sometimes arrives *inside* `text`
 * (`{"posts":[{"content":"…","hashtags":[…]}],"strategy_notes":"…"}`), and an
 * object can arrive where a string belongs — rendering that directly is what
 * turned the Review step into a white screen, and passing it on stores raw JSON
 * as the published caption.
 *
 * So: always hand back strings, unwrap the envelope when it is recognisable,
 * and say when the payload could not be understood instead of pretending.
 */

const MAX_FALLBACK_LENGTH = 4000;

function firstString(...candidates) {
  return candidates.find((c) => typeof c === "string" && c.trim() !== "") || "";
}

function normaliseHashtags(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((t) => typeof t === "string" && t.trim() !== "")
    .map((t) => t.trim());
}

/**
 * Try to read the model's JSON envelope out of a string.
 *
 * @param {string} value - Candidate text.
 * @returns {{text: string, hashtags: Array<string>}|null} The unwrapped post, or
 *   null when the string is not such an envelope.
 */
function unwrapEnvelope(value) {
  const trimmed = value.trim();
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }

  const candidate = Array.isArray(parsed)
    ? parsed[0]
    : Array.isArray(parsed?.posts)
    ? parsed.posts[0]
    : parsed;

  if (!candidate || typeof candidate !== "object") return null;

  const text = firstString(candidate.content, candidate.text, candidate.caption);
  if (!text) return null;

  return { text, hashtags: normaliseHashtags(candidate.hashtags || candidate.tags) };
}

/**
 * Normalise a generated post's content into something safe to render and send.
 *
 * @param {Object} post - Generated post as it came from the generator.
 * @returns {{text: string, title: string, hashtags: Array<string>,
 *   recovered: boolean, unparsed: boolean}} Strings only. `recovered` marks a
 *   payload that had to be unwrapped, `unparsed` one we could not make sense of.
 */
export function normaliseGeneratedContent(post) {
  const content = post?.content;

  if (content === null || content === undefined) {
    return { text: "", title: "", hashtags: [], recovered: false, unparsed: true };
  }

  if (typeof content === "string") {
    const unwrapped = unwrapEnvelope(content);
    if (unwrapped) {
      return { ...unwrapped, title: "", recovered: true, unparsed: false };
    }
    return { text: content, title: "", hashtags: [], recovered: false, unparsed: false };
  }

  if (typeof content !== "object") {
    return {
      text: String(content),
      title: "",
      hashtags: [],
      recovered: false,
      unparsed: true,
    };
  }

  const title = firstString(content.title);
  const hashtags = normaliseHashtags(content.hashtags || content.tags);

  if (typeof content.text === "string") {
    const unwrapped = unwrapEnvelope(content.text);
    if (unwrapped) {
      return {
        text: unwrapped.text,
        title,
        hashtags: hashtags.length ? hashtags : unwrapped.hashtags,
        recovered: true,
        unparsed: false,
      };
    }
    return { text: content.text, title, hashtags, recovered: false, unparsed: false };
  }

  // An object (or anything else) where the caption belongs: dig one level for a
  // usable string before giving up.
  const nested =
    content.text && typeof content.text === "object"
      ? firstString(content.text.content, content.text.text, content.text.caption)
      : "";

  if (nested) {
    return { text: nested, title, hashtags, recovered: true, unparsed: false };
  }

  return {
    text: JSON.stringify(content.text ?? content).slice(0, MAX_FALLBACK_LENGTH),
    title,
    hashtags,
    recovered: false,
    unparsed: true,
  };
}
